import { settings } from './../settings';

export class Clinic {
    constructor() {
        this.id = null;
        this.name = null;
    }
}

export class TimeSlot {
    constructor() {
        this.start = null;
        this.end = null;
        this.clinicId = null;
        this.clinicianId = null;
        this.treatmentId = null;
    }
}

export class Patient {
    constructor(personalNumberAsString = null, firstName = null, lastName = null) {
        this.firstName = firstName;
        this.lastName = lastName;

        this.personalNumberAsString = personalNumberAsString;

        this.email = null;
        this.mobilePhoneNumber = null;
    }
}

export class Clinician {
    constructor() {}
}

export class Treatment {
    constructor() {}
}

export class Filter {
    constructor() {
        this.clinic = new Clinic();
        this.clinician = new Clinician();
        this.treatment = new Treatment();
    }
}

export class Listing {
    constructor() {
        this.clinic = new Clinic();

        this.personalNumber = '';
        this.firstName = '';
        this.lastName = '';
        this.children = [];
        this.other = [];
        this.email = '';
        this.phone = '';

        this.relation = null;
    }
}

export class ListingInfo {
    constructor() {
        this.patientEmail = null;
        this.patientMessageSubject = null;
        this.patientMessageBody = null;
        this.clinicId = null;
        this.clinicMessageSubject = null;
        this.clinicMessageBody = null;
    }
}

export class ValueText {
    constructor(value, text) {
        this.value = value;
        this.text = text || null;
    }
}

export class Booking {
    constructor() {
        this.timeSlot = new TimeSlot();
        this.clinic = new Clinic();
        this.patient = new Patient();

        this.freeTextMessage = null;
        this.campaignCode = null;
        this.dentalFear = new ValueText(false);
        this.consentPersonalInformation = new ValueText(false);
        this.consentNewsAndOffers = new ValueText(false);
        this.isNewPatientBooking = false;
    }
}

export class BookingReschedule {
    constructor() {
        this.originalBookingId = null;
        this.timeSlot = new TimeSlot();
        this.patient = new Patient();
    }
}

export class BookingCancel {
    constructor() {
        this.booking = new Booking();
        this.clinic = new Clinic();
    }
}

export class Error {
    constructor(error, source) {
        this.source = source;
        this.status = error && error.status ? error.status : null;
        this.code = 0;
        this.details = null;
        this.readyState = error && error.readyState ? error.readyState : null;
        this.message = settings.resources.unknownError;
        this.handled = false;
        this.origin = error;
    }
}

export class Response {
    constructor(source, result) {
        this.source = source;
        this.result = result || null;
        this.error = new Error();
    }

    failed(error) {
        this.error = new Error(error, this.source);

        if (this.error.status) {
            const path = 'error_servicecall_failed';

            this.error.message =
                settings.settings.acf[`${path}_${this.error.status}`] ||
                `${settings.settings.acf[path]} (${this.error.status}: ${this.error.statusText})`;
        } else {
            this.error.message = settings.settings.acf[`error_generic`];
        }

        if (error.responseJSON) {
            this.error.details = JSON.stringify(error.responseJSON);
        } else if (error.responseText) {
            this.error.details = error.responseText;
        }

        return this;
    }
}

export class AuthResponse extends Response {
    constructor(result) {
        super('auth', result);
    }

    failed(error) {
        super.failed(error);

        if (error.responseJSON && error.responseJSON.errorCode) {
            this.error.code = error.responseJSON.errorCode;
            this.error.details = error.responseJSON.errorDetails;

            const path = 'bankid_message_failed';

            switch (this.error.code) {
                case 1:
                    this.error.message = settings.settings.acf[`${path}_alreadyInProgress`];
                    break;
                case 2:
                    this.error.message = settings.settings.acf[`${path}_invalidParameters`];
                    break;
                case 3:
                    this.error.message = settings.settings.acf[`${path}_cancelled`];
                    break;
                case 4:
                    this.error.message = settings.settings.acf[`${path}_unauthorized`];
                    break;
                case 5:
                    this.error.message = settings.settings.acf[`${path}_notFound`];
                    break;
                case 6:
                    this.error.message = settings.settings.acf[`${path}_requestTimeout`];
                    break;
                case 9:
                    this.error.message = settings.settings.acf[`${path}_maintenance`];
                    break;
                default:
                    this.error.message = settings.settings.acf[`${path}_internalError`];
                    break;
            }
        }

        return this;
    }
}

export class CollectResponse extends Response {
    constructor(result) {
        super('collect', result);

        this.code = null;
        this.message = null;

        this.parse();
    }

    parse() {
        if (this.result.response) {
            if (this.result.response.status == 'pending') {
                this.code = this.result.response.hintCode;
                this.message = settings.settings.acf[`bankid_message_pending_${this.code}`];
            } else if (this.result.response.status == 'failed') {
                this.code = this.result.response.errorCode || this.result.response.hintCode;
                this.message = settings.settings.acf[`bankid_message_failed_${this.code}`];
            }
        }
    }

    failed(error) {
        super.failed(error);

        return this;
    }
}

export class OpusResponse extends Response {
    constructor(result) {
        super('opus', result);

        this.error.apiUrl = null;
        this.validationErrors = [];
    }

    failed(error, apiUrl, customError) {
        super.failed(error);

        this.error.apiUrl = apiUrl;

        const path = 'opus_error';

        if (error.responseJSON && error.responseJSON.errors) {
            const fix = (code) => {
                return code.charAt(0).toLowerCase() + code.substring(1).replace(/\./g, '');
            };

            for (var code in error.responseJSON.errors) {
                if (error.responseJSON.errors.hasOwnProperty(code)) {
                    code = fix(code);

                    this.validationErrors.push({
                        code: code,
                        message: settings.settings.acf[`${path}_validation_${code}`],
                    });
                }
            }
        }

        if (this.validationErrors.length == 0 && customError) {
            this.error.message = settings.settings.acf[`${path}_${customError}`];
        }

        return this;
    }
}

export class ClaimsResponse extends Response {
    constructor(result) {
        super('claims', result);
    }

    failed(error) {
        super.failed(error);

        if (error.status && error.status == 401) {
            // Expected, do not log
            this.error.handled = true;
        }

        return this;
    }
}

export class ConfigurationResponse extends Response {
    constructor(result) {
        super('configuration', result);
    }

    failed(error) {
        super.failed(error);

        return this;
    }
}

export class PriceResponse extends Response {
    constructor(result) {
        super('price', result);
    }

    failed(error) {
        super.failed(error);

        return this;
    }
}



// WEBPACK FOOTER //
// ./scripts/booking/api/models.js